<template>
  <v-card :loading="loading" min-height="200px" variant="outlined" class="metric-title bg-white">
    <v-card-text>
      <v-row class="align-center">
        <v-col class="d-flex align-center" cols="9">
          <div class="pa-4 d-inline text-center rounded bg-green mr-4">
            <v-icon color="white" size="small">fa-solid fa-clock</v-icon>
          </div>
          <h1 class="font-weigh-bold">
            New Signup Account Types
          </h1>
        </v-col>
      </v-row>

      <v-row v-if="!loading && (!metric || !metric.count || !metric.accountTypes)" class="mt-4">
        <v-col class="text-center text-gray">
          <h2>No Data</h2>
        </v-col>
      </v-row>

      <v-row  v-show="metric && metric.count && metric.accountTypes" class="align-center">
        <v-col cols="3">
          <canvas id="accountTypesChart"></canvas>
        </v-col>

        <v-col cols="9">
          <v-row v-if="!loading && metric && metric.accountTypes" dense no-gutters>
            <v-col v-for="(count, index) in metric.accountTypes" :key="index" cols="6" xl="4">
              <div class="d-flex align-center">
                <div
                  :style="`background-color: ${ACCOUNT_COLORS[index]}; width: 15px; height: 15px; border-radius: 50%; margin-right: 8px;`"></div>
                <div>
                  <div class="mt-2 font-weight-bold">{{ $t(`accountTypes.${index}`) }}</div>
                  <div class="caption">{{ count }} users ({{ Math.round((count / metric.count) * 100) }}%)</div>
                </div>
              </div>
            </v-col>

          </v-row>

        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { onMounted, watchEffect, ref } from "vue";
import { Chart, registerables, ChartConfiguration } from "chart.js";
import { useI18n } from "vue-i18n";
import {ACCOUNT_COLORS} from "@/lib/colors";
import {useMetricTile} from "@/composables/useMetricTile";
const { metric, loading } = useMetricTile<{
  count: number;
  accountTypes: Record<string, number>;
}>("users-created-over-time");
// Register Chart.js components
Chart.register(...registerables);

interface ChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

// Composables
const { t } = useI18n();
const chartInstance = ref<Chart | null>(null);

// Functions
const getChartCanvas = (): HTMLCanvasElement | null => {
  const canvas = document.getElementById("accountTypesChart");
  return canvas instanceof HTMLCanvasElement ? canvas : null;
};

const clearExistingChart = (canvas: HTMLCanvasElement): void => {
  const existingChart = Chart.getChart(canvas);
  if (existingChart) {
    existingChart.destroy();
  }
};

const createChartData = (): ChartData => ({
  labels: Object.keys(metric.value.accountTypes).map((type) => t(`accountTypes.${type}`)),
  datasets: [{
    data: Object.values(metric.value.accountTypes),
    backgroundColor: Object.keys(metric.value.accountTypes).map((type) => ACCOUNT_COLORS[type])
  }]
});

const getChartConfig = (data: ChartData): ChartConfiguration => ({
  type: "doughnut",
  data,
  options: {
    responsive: true,
    plugins: {
      legend: {
        position: "top"
      }
    }
  }
});

const initializeChart = (): void => {
  const canvas = getChartCanvas();

  if (!canvas || !metric.value?.accountTypes) return;

  clearExistingChart(canvas);
  canvas.style.maxHeight = "250px";

  const chartData = createChartData();
  const config = getChartConfig(chartData);
  chartInstance.value = new Chart(canvas, config);
};

// Lifecycle hooks
onMounted(() => {
  watchEffect(() => {
    if (!loading.value && metric.value?.accountTypes) {
      initializeChart();
    }
  });
});
</script>
